(() => {
  "use strict";

  const config = window.KixboxMerchRankingConfig;
  if (!config || !config.enabled || !config.endpoint || !config.publishableKey) return;

  const SESSION_KEY = "kixbox_merch_session_v1";
  const CARD_SELECTOR = "[data-recs-product-card]";
  const state = {
    tracking: false,
    sessionId: null,
    queue: [],
    sending: false,
    impressionObserver: null,
    gridObserver: null,
    observedCards: new WeakSet(),
    productViewSent: false,
    flushTimer: null,
  };

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }

  function getSessionId() {
    if (state.sessionId) return state.sessionId;
    try {
      state.sessionId = window.sessionStorage.getItem(SESSION_KEY);
      if (!state.sessionId) {
        state.sessionId = uuid();
        window.sessionStorage.setItem(SESSION_KEY, state.sessionId);
      }
    } catch (_error) {
      state.sessionId = uuid();
    }
    return state.sessionId;
  }

  function clearSession() {
    state.sessionId = null;
    try {
      window.sessionStorage.removeItem(SESSION_KEY);
    } catch (_error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  function positiveInteger(value) {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function cardPosition(card) {
    const explicit = positiveInteger(card.dataset.merchPosition);
    if (explicit) return explicit;
    const grid = card.closest("#product-grid, .product-grid");
    if (!grid) return null;
    return Array.from(grid.querySelectorAll(CARD_SELECTOR)).indexOf(card) + 1 || null;
  }

  function cardEvent(card, eventType) {
    const productId = card && card.dataset.shopifyProductId;
    if (!productId) return null;
    return {
      event_type: eventType,
      product_id: productId,
      variant_id: card.dataset.shopifyVariantId || null,
      context_type: card.dataset.merchContextType || config.contextType || "unknown",
      context_id: card.dataset.merchContextId || config.contextId || null,
      collection_handle: card.dataset.merchCollectionHandle || config.collectionHandle || null,
      placement: card.dataset.merchPlacement || "product_card",
      position: cardPosition(card),
    };
  }

  function enqueue(event) {
    if (!state.tracking || !event || !event.product_id) return;
    state.queue.push({
      event_id: uuid(),
      occurred_at: new Date().toISOString(),
      ...event,
    });
    scheduleFlush(state.queue.length >= 20 ? 0 : 1500);
  }

  function scheduleFlush(delay) {
    if (state.flushTimer) window.clearTimeout(state.flushTimer);
    state.flushTimer = window.setTimeout(flush, delay);
  }

  async function flush() {
    state.flushTimer = null;
    if (!state.tracking || state.sending || state.queue.length === 0) return;
    const batch = state.queue.slice(0, 20);
    state.sending = true;
    try {
      const response = await window.fetch(config.endpoint, {
        method: "POST",
        mode: "cors",
        credentials: "omit",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "x-publishable-api-key": config.publishableKey,
        },
        body: JSON.stringify({
          shop: config.shop,
          session_id: getSessionId(),
          events: batch,
        }),
      });
      if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
        state.queue.splice(0, batch.length);
      }
    } catch (_error) {
      // Analytics must never interfere with storefront actions.
    } finally {
      state.sending = false;
      if (state.tracking && state.queue.length) scheduleFlush(5000);
    }
  }

  function bindCards(root = document) {
    if (!state.impressionObserver) return;
    root.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (state.observedCards.has(card)) return;
      state.observedCards.add(card);
      state.impressionObserver.observe(card);
    });
  }

  function startObservers() {
    if ("IntersectionObserver" in window) {
      state.impressionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
            state.impressionObserver.unobserve(entry.target);
            enqueue(cardEvent(entry.target, "product_impression"));
          });
        },
        { threshold: 0.5 },
      );
      bindCards();
    }

    const grid = document.getElementById("ProductGridContainer") || document.body;
    state.gridObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches(CARD_SELECTOR)) bindCards(node.parentElement || document);
          else if (node.querySelector) bindCards(node);
        });
      });
    });
    state.gridObserver.observe(grid, { childList: true, subtree: true });
  }

  function stopObservers() {
    if (state.impressionObserver) state.impressionObserver.disconnect();
    if (state.gridObserver) state.gridObserver.disconnect();
    state.impressionObserver = null;
    state.gridObserver = null;
    state.observedCards = new WeakSet();
  }

  function trackProductView() {
    if (state.productViewSent || !config.productId) return;
    state.productViewSent = true;
    enqueue({
      event_type: "product_view",
      product_id: config.productId,
      variant_id: config.variantId || null,
      context_type: "product",
      context_id: config.productId,
      collection_handle: config.collectionHandle || null,
      placement: "pdp",
      position: null,
    });
  }

  function startTracking() {
    if (state.tracking) return;
    state.tracking = true;
    getSessionId();
    startObservers();
    trackProductView();
  }

  function stopTracking() {
    if (!state.tracking) return;
    state.tracking = false;
    stopObservers();
    state.queue = [];
    if (state.flushTimer) window.clearTimeout(state.flushTimer);
    state.flushTimer = null;
    clearSession();
  }

  function syncConsent() {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    let allowed = false;
    try {
      allowed = Boolean(privacy && privacy.analyticsProcessingAllowed());
    } catch (_error) {
      allowed = false;
    }
    if (allowed) startTracking();
    else stopTracking();
  }

  document.addEventListener(
    "click",
    (event) => {
      if (!state.tracking || !(event.target instanceof Element)) return;
      const link = event.target.closest(`${CARD_SELECTOR} a[href*="/products/"]`);
      if (!link) return;
      enqueue(cardEvent(link.closest(CARD_SELECTOR), "product_click"));
      flush();
    },
    true,
  );

  document.addEventListener(
    "submit",
    (event) => {
      if (!state.tracking || !(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      if (!String(form.action || "").includes("/cart/add")) return;
      const card = form.closest(CARD_SELECTOR);
      const productId = (card && card.dataset.shopifyProductId) || config.productId;
      if (!productId) return;
      let variantId = (card && card.dataset.shopifyVariantId) || config.variantId || null;
      try {
        variantId = new FormData(form).get("id") || variantId;
      } catch (_error) {
        // Keep the rendered variant fallback.
      }
      enqueue({
        ...(cardEvent(card, "add_to_cart") || {}),
        event_type: "add_to_cart",
        product_id: productId,
        variant_id: variantId,
        context_type: config.contextType || "product",
        context_id: config.contextId || productId,
        placement: card ? "product_card" : "pdp",
      });
      flush();
    },
    true,
  );

  document.addEventListener("visitorConsentCollected", syncConsent);
  window.addEventListener("pagehide", flush);

  if (window.Shopify && typeof window.Shopify.loadFeatures === "function") {
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (!error) syncConsent();
    });
  }
})();
